import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
// @mui
import { styled, useTheme } from "@mui/material/styles";
import {
  Box,
  Stack,
  Drawer,
  Link,
  Typography,
  IconButton,
} from "@mui/material";
// hooks
import useAuth from "../../../hooks/useAuth";
import useResponsive from "../../../hooks/useResponsive";
import useCollapseDrawer from "../../../hooks/useCollapseDrawer";
import { Trans, useTranslation } from "react-i18next";
// contexts
import useStore from "../../../contexts/JWTContext";
// utils
import cssStyles from "../../../utils/cssStyles";
// config
import { NAVBAR } from "../../../config";
// components
import Scrollbar from "../../../components/Scrollbar";
import { NavSectionVertical } from "../../../components/nav-section";
//
import Iconify from "../../../components/Iconify";
import navConfig from "./NavConfig";
import navGuestConfig from "./NavGuestConfig";
import navAdminConfig from "./NavAdminConfig";
import navSpecialAdminConfig from "./NavSpecialAdminConfig";
import NavbarAccount from "./NavbarAccount";
// images
import Logo from "../../../assets/logo_domino.png";
import LogoEN from "../../../assets/logo_domino_eng.png";

import LanguagePopover from "./LanguagePopover";
import AccountPopover from "./AccountPopover";

// ----------------------------------------------------------------------

const RootStyle = styled("div")(({ theme }) => ({
  [theme.breakpoints.up("sm")]: {
    flexShrink: 0,
    transition: theme.transitions.create("width", {
      duration: theme.transitions.duration.shorter,
    }),
  },
}));

// ----------------------------------------------------------------------

NavbarVertical.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
};

export default function NavbarVertical({
  isOpenSidebar,
  onCloseSidebar,
  isOpen,
  handleToggle,
}) {
  const { getSpecialAdmin } = useStore();
  const theme = useTheme();
  const { i18n, t } = useTranslation();
  const isDesktop = useResponsive("up", "sm");
  const { role } = useAuth();
  const { pathname } = useLocation();
  const { quotation_id } = useParams();

  const [plannerLink, setPlannerLink] = useState("/dashboard/planner/create");
  const [specialAdmin, setSpecialAdmin] = useState(null);

  useEffect(() => {
    if (role === "ADMIN") {
      const specialAdmin = getSpecialAdmin();
      if (specialAdmin) {
        setSpecialAdmin(specialAdmin);
      }
    }
  }, []);

  useEffect(() => {
    if (quotation_id) {
      setPlannerLink(`/dashboard/planner/${quotation_id}/modify`);
    } else {
      setPlannerLink("/dashboard/planner/create");
    }
  }, [quotation_id]);

  const {
    isCollapse,
    collapseClick,
    collapseHover,
    onToggleCollapse,
    onHoverEnter,
    onHoverLeave,
  } = useCollapseDrawer();

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        "& .simplebar-content": {
          height: 1,
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Stack
        spacing={3}
        sx={{
          pt: 3,
          pb: 2,
          px: 2.5,
          flexShrink: 0,
          ...(isCollapse && { alignItems: "center" }),
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box
            component="img"
            src={i18n.resolvedLanguage === "it-IT" ? Logo : LogoEN}
            alt="Logo"
            sx={{
              width: "100%",
              height: "auto",
              objectFit: "contain",
              objectPosition: "center",
            }}
          />
        </Stack>

        <NavbarAccount />
      </Stack>

      <NavSectionVertical
        navConfig={
          role === "ADMIN"
            ? specialAdmin
              ? navSpecialAdminConfig(plannerLink)
              : navAdminConfig(plannerLink)
            : role === "GUEST"
            ? navGuestConfig(plannerLink)
            : navConfig(plannerLink)
        }
        isCollapse={isCollapse}
      />

      <Box sx={{ pt: 3, px: 2.5, flexShrink: 0 }}>
        <hr
          style={{
            borderColor: "#ffffff",
            height: 1,
            borderBottom: 0,
            borderLeft: 0,
            borderRight: 0,
          }}
        />
        <Box sx={{ pt: 3, display: "flex", flexDirection: "column" }}>
          <Box sx={{ display: "flex", flexFlow: "wrap" }}>
            <Typography
              variant="body2"
              sx={{
                fontSize: ".8rem",
                fontStyle: "italic",
              }}
            >
              {t("settings.drawer.assistance.helpline")
                .split("\n")
                .map((c, index) => {
                  return <p key={index}> {c} </p>;
                })}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                fontSize: ".8rem",
                fontStyle: "italic",
              }}
            >
              {t("settings.drawer.assistance.helpline_after")
                .split("\n")
                .map((c, index) => {
                  return <p key={index}> {c} </p>;
                })}
            </Typography>
          </Box>

          <Typography
            variant="body2"
            sx={{ fontSize: ".8rem", fontStyle: "italic" }}
          >
            {t("settings.drawer.assistance.active_days")}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ flexGrow: 1, pt: 3, pb: 2, px: 2.5, flexShrink: 0 }}>
        <hr
          style={{
            borderColor: "#ffffff",
            height: 1,
            borderBottom: 0,
            borderLeft: 0,
            borderRight: 0,
          }}
        />
        <Box sx={{ pt: 3, display: "flex" }}>
          {role !== "GUEST" && <AccountPopover />}

          {(role === "GUEST" || role === "ADMIN") && (
            <Box sx={role === "ADMIN" && { ml: "auto" }}>
              <LanguagePopover />
            </Box>
          )}
        </Box>
      </Box>

      {/* Place almaware logo on the bottom */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          pt: 3,
          pb: 2,
          px: 2.5,
          flexShrink: 0,
          justifyContent: "flex-end",
          alignItems: "center",
        }}
      >
        {/*<Typography*/}
        {/*  variant="body2"*/}
        {/*  sx={{ fontSize: "12px", color: "text.secondary" }}*/}
        {/*>*/}
        {/*  Sviluppato da*/}
        {/*</Typography>*/}
        {/*<Link href="https://almaware.net/" target="_blank">*/}
        {/*  <Box*/}
        {/*    component="img"*/}
        {/*    src="/assets/almaware_logo.jpeg"*/}
        {/*    alt="Almaware"*/}
        {/*    sx={{*/}
        {/*      width: "90px",*/}
        {/*    }}*/}
        {/*  />*/}
        {/*</Link>*/}
      </Box>
    </Scrollbar>
  );

  return (
    <RootStyle
      sx={{
        position: "absolute",
        width: {
          lg: isCollapse
            ? NAVBAR.DASHBOARD_COLLAPSE_WIDTH
            : NAVBAR.DASHBOARD_WIDTH,
        },
        ...(collapseClick && {
          position: "absolute",
        }),
      }}
    >
      {/* {!isDesktop && (
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{ sx: { width: NAVBAR.DASHBOARD_WIDTH } }}
        >
          {renderContent}
        </Drawer>
      )} */}

      {isDesktop && (
        <Drawer
          open
          variant="persistent"
          onMouseEnter={onHoverEnter}
          onMouseLeave={onHoverLeave}
          PaperProps={{
            sx: {
              width: isOpen
                ? NAVBAR.DASHBOARD_WIDTH + 65
                : NAVBAR.DASHBOARD_WIDTH - 178,
              // borderRightStyle: "dashed",
              bgcolor: "background.default",
              transition: (theme) =>
                theme.transitions.create("width", {
                  duration: theme.transitions.duration.standard,
                }),
              ...(isCollapse && {
                width: NAVBAR.DASHBOARD_COLLAPSE_WIDTH,
              }),
              ...(collapseHover && {
                ...cssStyles(theme).bgBlur(),
                boxShadow: (theme) => theme.customShadows.z24,
              }),
            },
          }}
        >
          <Box sx={{ height: "100%", display: "flex", alignItems: "center" }}>
            <>{isOpen && renderContent}</>
            <IconButton
              onClick={handleToggle}
              sx={{ margin: "0 5px 0 5px", p: 2 }}
            >
              <Iconify
                icon={
                  isOpen
                    ? "eva:arrow-back-outline"
                    : "eva:arrow-forward-outline"
                }
                width={20}
                height={20}
              />
            </IconButton>
          </Box>
        </Drawer>
      )}
    </RootStyle>
  );
}
